// src/components/LabManagement.js - Versão corrigida
import React, { useState, useEffect } from 'react';
import {
  listMyLaboratories, listAllLaboratories, setActiveLaboratory, archiveLaboratory,
  deleteLaboratory, deleteLabMembers, listLabMembers, removeLabMember,
  fetchLabMemberProfiles, createLaboratory as insertLaboratory, addLabMember,
  countLabContents, atualizarMembro,
} from '../data/labs';
import { usuarioDeEmail, normalizarUsuario } from '../data/usuario';
import {
  listTechnicians, insertTechnician, deactivateTechnician, reactivateTechnician,
  deleteTechnician,
} from '../data/technicians';
import { callEdgeFunction } from '../data/edge';
import ContasBancarias from './FIVSystem/shared/ContasBancarias';
import ConfiguracaoDoLaboratorio from './FIVSystem/shared/ConfiguracaoDoLaboratorio';
import LinkDoCliente from './FIVSystem/shared/LinkDoCliente';
import { Plus, Users, Crown, User, Eye, Stethoscope, Wrench, Trash2, UserPlus, HardHat } from 'lucide-react';
import { MARK } from '../constants';
import JanelaModal from './JanelaModal';

const LabManagement = ({ user, isSuperAdmin = false, currentLab, onLabChange, onLabSaved }) => {
  const [laboratories, setLaboratories] = useState([]);
  const [labMembers, setLabMembers] = useState([]);
  const [showCreateLab, setShowCreateLab] = useState(false);
  const [showAddUser, setShowAddUser] = useState(false);
  const [loading, setLoading] = useState(false);
  const [newLabName, setNewLabName] = useState('');
  const [newUser, setNewUser] = useState({
    username: '',
    password: '',
    full_name: '',
    role: 'moderator'
  });
  const [addUserError, setAddUserError] = useState('');
  const [removeTarget, setRemoveTarget] = useState(null);
  const [technicians, setTechnicians] = useState([]);
  const [newTechnician, setNewTechnician] = useState({ name: '', document: '', contact: '' });
  const [technicianError, setTechnicianError] = useState('');
  // Técnico com o painel do link aberto. Um de cada vez: o painel mostra senha.
  const [linkAberto, setLinkAberto] = useState(null);

  const roleNames = {
    admin: { name: 'Administrador', icon: Crown, color: 'text-slate-600' },
    moderator: { name: 'Moderador', icon: Stethoscope, color: 'text-slate-600' },
    technician: { name: 'Técnico', icon: Wrench, color: 'text-slate-600' },
    viewer: { name: 'Visualizador', icon: Eye, color: 'text-slate-600' }
  };

  useEffect(() => {
    loadUserLaboratories();
    if (currentLab) {
      loadLabMembers();
      loadTechnicians();
    }
  }, [currentLab]);

  // As duas cargas devolvem formatos diferentes (`lab_members[0].role` de um
  // lado, papel nenhum do outro). Normalizar aqui em `user_role` evita espalhar
  // o `if` pela tela.
  const loadUserLaboratories = async () => {
    try {
      if (isSuperAdmin) {
        const todos = await listAllLaboratories() || [];
        setLaboratories(todos.map(lab => ({ ...lab, user_role: 'admin' })));
        return;
      }
      const meus = await listMyLaboratories(user.id) || [];
      setLaboratories(meus.map(lab => ({ ...lab, user_role: lab.lab_members?.[0]?.role })));
    } catch (error) {
      console.error('Erro ao carregar laboratórios:', error);
    }
  };

  // Conta o que está vinculado ao laboratório para decidir entre arquivar
  // (dados preservados) e excluir de vez (só quando não há nada a perder).
  const openRemoveLab = async (lab) => {
    setRemoveTarget({ lab, counts: null, loading: true });
    try {
      const counts = await countLabContents(lab.id);

      setRemoveTarget({ lab, counts, loading: false });
    } catch (error) {
      console.error('Erro ao contar dados do laboratório:', error);
      setRemoveTarget({ lab, counts: null, loading: false, error: error.message });
    }
  };

  // Depois de tirar um lab da lista, garante que o usuário não fique parado
  // num laboratório que não existe mais.
  const afterLabRemoved = async (removedId) => {
    const restantes = laboratories.filter(l => l.id !== removedId);
    setRemoveTarget(null);
    await loadUserLaboratories();

    if (currentLab?.id === removedId) {
      const proximo = restantes[0] || null;
      if (proximo) {
        await setActiveLaboratory(user.id, proximo.id);
      }
      onLabChange(proximo);
    }
  };

  const archiveLab = async () => {
    const lab = removeTarget?.lab;
    if (!lab) return;

    setLoading(true);
    try {
      await archiveLaboratory(lab.id);
      await afterLabRemoved(lab.id);
      alert(`"${lab.name}" foi arquivado. Os dados continuam salvos e é possível reativá-lo.`);
    } catch (error) {
      console.error('Erro ao arquivar laboratório:', error);
      alert('Erro ao arquivar: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const deleteLabPermanently = async () => {
    const lab = removeTarget?.lab;
    if (!lab || removeTarget?.counts?.total !== 0) return;

    setLoading(true);
    try {
      await deleteLabMembers(lab.id);
      await deleteLaboratory(lab.id);
      await afterLabRemoved(lab.id);
      alert(`"${lab.name}" foi excluído.`);
    } catch (error) {
      console.error('Erro ao excluir laboratório:', error);
      alert('Erro ao excluir: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadLabMembers = async () => {
    try {
      console.log('Carregando membros do lab:', currentLab.id);

      const members = await listLabMembers(currentLab.id);

      if (members && members.length > 0) {
        const profiles = await fetchLabMemberProfiles(currentLab.id);

        // Reserva quando o perfil não vem: mostra o email se houver, e um
        // rótulo neutro caso contrário. O código antigo inventava
        // "email@exemplo.com" — dado falso apresentado como real é pior que
        // campo vazio, porque alguém acaba tentando usá-lo.
        const membersWithProfiles = members
          .map(member => {
            const perfil = profiles?.find(p => p.id === member.user_id);
            return {
              ...member,
              profiles: perfil || {
                full_name: 'Nome não disponível',
                email: null,
              },
            };
          })
          // A conta de super admin atravessa todos os laboratórios; não é uma
          // pessoa da equipe de nenhum. Listá-la aqui misturava manutenção com
          // time, e ainda somava uma pessoa a mais na contagem.
          .filter(member => !member.profiles?.is_super_admin);

        setLabMembers(membersWithProfiles);
      } else {
        setLabMembers([]);
      }

    } catch (error) {
      console.error('Erro ao carregar membros:', error);
      setLabMembers([]);
    }
  };

  const loadTechnicians = async () => {
    try {
      setTechnicians(await listTechnicians(currentLab.id) || []);
    } catch (error) {
      console.error('Erro ao carregar técnicos:', error);
      setTechnicians([]);
    }
  };

  const addTechnician = async () => {
    const name = newTechnician.name.trim();
    if (!name) return;

    setLoading(true);
    setTechnicianError('');
    try {
      await insertTechnician({
        laboratory_id: currentLab.id,
        name,
        document: newTechnician.document.trim() || null,
        contact: newTechnician.contact.trim() || null,
      });
      setNewTechnician({ name: '', document: '', contact: '' });
      loadTechnicians();
    } catch (error) {
      console.error('Erro ao cadastrar técnico:', error);
      // 23505 é a unicidade por laboratório: nome repetido é quase sempre
      // cadastro em duplicata, e duplicata aqui espalha para toda sessão.
      setTechnicianError(error.code === '23505'
        ? 'Já existe um técnico com esse nome neste laboratório.'
        : error.message);
    } finally {
      setLoading(false);
    }
  };

  // Desativa em vez de excluir: o nome fica gravado nas sessões já feitas.
  const toggleTechnician = async (tecnico) => {
    try {
      if (tecnico.active) {
        await deactivateTechnician(tecnico.id);
      } else {
        await reactivateTechnician(tecnico.id);
      }
      loadTechnicians();
    } catch (error) {
      console.error('Erro ao alterar técnico:', error);
      alert('Erro ao alterar técnico: ' + error.message);
    }
  };

  /**
   * Excluir de vez, para cadastro errado ou duplicado.
   *
   * Seguro porque a sessão grava o NOME do técnico junto com o id: o registro
   * de quem executou continua legível sem a linha. Desativar segue ao lado,
   * para quem apenas saiu da equipe — são coisas diferentes.
   */
  const removeTechnician = async (tecnico) => {
    const aviso = [
      `Excluir "${tecnico.name}" do cadastro?`,
      '',
      'Os lançamentos já feitos continuam mostrando o nome dele.',
      'Se a pessoa apenas saiu da equipe, prefira Desativar.',
    ].join('\n');
    if (!window.confirm(aviso)) return;

    try {
      await deleteTechnician(tecnico.id);
      loadTechnicians();
    } catch (error) {
      console.error('Erro ao excluir técnico:', error);
      alert('Erro ao excluir técnico: ' + error.message);
    }
  };

  const createLaboratory = async () => {
    if (!newLabName.trim()) return;

    setLoading(true);
    try {
      const lab = await insertLaboratory({
        name: newLabName,
        owner_id: user.id,
        description: `Laboratório criado por ${usuarioDeEmail(user.email)}`,
      });

      await addLabMember({ laboratory_id: lab.id, user_id: user.id, role: 'admin' });

      await setActiveLaboratory(user.id, lab.id);

      setNewLabName('');
      setShowCreateLab(false);
      loadUserLaboratories();
      onLabChange(lab);
      alert('Laboratório criado com sucesso!');
    } catch (error) {
      console.error('Erro ao criar laboratório:', error);
      alert('Erro ao criar laboratório: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Cadastro de usuário pelo admin do laboratório.
  // A criação da conta acontece na Edge Function admin-create-user, que roda
  // com service_role e valida pelo JWT que quem chamou é admin deste lab.
  const addUser = async () => {
    const username = normalizarUsuario(newUser.username);
    if (!username || !newUser.password) return;

    setLoading(true);
    setAddUserError('');
    try {
      const { data: result } = await callEdgeFunction('admin-create-user', {
        username,
        password: newUser.password,
        full_name: newUser.full_name.trim(),
        role: newUser.role,
        laboratory_id: currentLab.id,
      }, { requireSession: true });

      if (!result.success) throw new Error(result.error || 'Falha ao cadastrar usuário');

      setNewUser({ username: '', password: '', full_name: '', role: 'moderator' });
      setShowAddUser(false);
      loadLabMembers();
      alert(
        result.data?.created
          ? 'Usuário cadastrado e adicionado à equipe!'
          : 'Usuário já existia e foi adicionado à equipe!'
      );
    } catch (error) {
      console.error('Erro ao cadastrar usuário:', error);
      setAddUserError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const removeMember = async (memberId, memberName) => {
    if (!window.confirm(`Remover ${memberName} do laboratório?`)) return;

    try {
      await removeLabMember(memberId);
      loadLabMembers();
      alert('Membro removido com sucesso!');
    } catch (error) {
      console.error('Erro ao remover membro:', error);
      alert('Erro ao remover membro: ' + error.message);
    }
  };

  /**
   * Muda função ou situação de um membro.
   *
   * As regras (dono intocável, nunca ficar sem admin) moram no banco, então
   * aqui a mensagem de erro dele é mostrada como está — foi escrita para ser
   * lida por quem clicou, e reescrevê-la aqui só criaria duas versões da mesma
   * regra para sair de sincronia.
   */
  const alterarMembro = async (member, mudanca) => {
    try {
      await atualizarMembro(member.id, mudanca);
      loadLabMembers();
    } catch (error) {
      console.error('Erro ao alterar membro:', error);
      alert(error.message);
    }
  };

  const switchLaboratory = async (lab) => {
    try {
      await setActiveLaboratory(user.id, lab.id);

      onLabChange(lab);
    } catch (error) {
      console.error('Erro ao trocar laboratório:', error);
    }
  };

  const userRole = labMembers.find(m => m.user_id === user.id)?.role || 'viewer';
  // O super admin não aparece em `labMembers` de lugar nenhum — sem esta
  // exceção ele cairia em 'viewer' e ficaria sem o botão de cadastrar usuário
  // justamente nos laboratórios que só ele alcança.
  const isAdmin = isSuperAdmin || userRole === 'admin';
  // Técnico (e ajudante, que também é técnico) é cadastro do dia a dia: o
  // moderador já cria serviço e escolhe quem vai, e precisava pedir ao admin
  // cada pessoa nova.
  const podeCadastrarTecnico = isAdmin || userRole === 'moderator';
  // O link da agenda segue a regra do link do cliente, que o banco confere:
  // admin ou veterinário. O moderador cadastra o técnico, mas não entrega acesso.
  const podeGerarLink = isAdmin || userRole === 'veterinary';

  // Resto do componente continua igual...
  return (
    <div className="space-y-6">
      {/* Seletor de Laboratório */}
      <div className="bg-white rounded-lg p-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-slate-900">Laboratórios Disponíveis</h3>
          <button
            onClick={() => setShowCreateLab(true)}
            className="flex items-center space-x-2 text-blue-600 hover:text-blue-900 text-sm"
          >
            <Plus size={16} />
            <span>Criar Novo</span>
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {laboratories.map(lab => (
            <div
              key={lab.id}
              className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                currentLab?.id === lab.id
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-slate-200 hover:border-slate-300'
              }`}
              onClick={() => switchLaboratory(lab)}
            >
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-slate-900">{lab.name}</h4>
                  <p className="text-sm text-slate-600">
                    Função: {roleNames[lab.user_role]?.name}
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  {currentLab?.id === lab.id && (
                    <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                  )}
                  {lab.owner_id === user.id && (
                    <button
                      onClick={(e) => { e.stopPropagation(); openRemoveLab(lab); }}
                      className="text-slate-400 hover:text-red-600 p-1"
                      title="Arquivar ou excluir laboratório"
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Dados, logo e preços padrão: admin e moderador. O dono entra mesmo
          sem aparecer em `labMembers`: a função do banco também o aceita. */}
      {currentLab && (
        <ConfiguracaoDoLaboratorio
          laboratorio={currentLab}
          podeMexer={isAdmin || userRole === 'moderator' || currentLab.owner_id === user.id}
          onSalvo={onLabSaved}
        />
      )}

      {currentLab && (
        <div className="bg-white rounded-lg shadow-sm">
          <div className="px-6 py-4 border-b flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Users className="text-slate-400" size={20} />
              <h3 className="text-lg font-medium text-slate-900">
                Equipe - {currentLab.name}
              </h3>
            </div>
            {isAdmin && (
              <button
                onClick={() => setShowAddUser(true)}
                className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
              >
                <UserPlus size={16} />
                <span>Cadastrar Usuário</span>
              </button>
            )}
          </div>

          <div className="divide-y divide-slate-200">
            {labMembers.map(member => {
              const RoleIcon = roleNames[member.role]?.icon || User;
              const ehDono = member.user_id === currentLab.owner_id;
              const inativo = member.status === 'inactive';
              // O dono não é editável: a regra vive no banco, e desabilitar
              // aqui evita oferecer um clique que só vai voltar com erro.
              const podeMexer = isAdmin && !ehDono;

              return (
                <div key={member.id}
                  className={`px-6 py-4 flex items-center justify-between gap-4 ${inativo ? 'bg-slate-50' : ''}`}>
                  <div className="flex items-center space-x-3 min-w-0">
                    <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center shrink-0">
                      <RoleIcon className={inativo ? 'text-slate-300' : roleNames[member.role]?.color} size={20} />
                    </div>
                    <div className="min-w-0">
                      <p className={`font-medium truncate ${inativo ? 'text-slate-400' : 'text-slate-900'}`}>
                        {member.profiles?.full_name || usuarioDeEmail(member.profiles?.email) || 'Nome não disponível'}
                      </p>
                      {/* Usuário numa linha própria: numa tela de equipe é ele
                          que identifica quem é quem entre homônimos, e antes
                          só aparecia quando o nome faltava. */}
                      {member.profiles?.email && member.profiles?.full_name && (
                        <p className="text-xs text-slate-500 truncate">
                          {usuarioDeEmail(member.profiles.email)}
                        </p>
                      )}
                      <p className="text-sm text-slate-600">
                        {/* Inativo é dito por escrito, não só pelo cinza: numa
                            lista de cinco pessoas o tom sozinho não se nota. */}
                        {inativo && <span className="text-slate-500">Inativo • </span>}
                        {!podeMexer && roleNames[member.role]?.name}
                        {ehDono && ' • Dono do Lab'}
                      </p>
                    </div>
                  </div>

                  {podeMexer && (
                    <div className="flex items-center gap-2 shrink-0">
                      <select
                        value={member.role}
                        onChange={(e) => alterarMembro(member, { role: e.target.value })}
                        className="px-2 py-1.5 border border-slate-200 rounded-lg text-sm text-slate-700
                                   focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="admin">Administrador</option>
                        <option value="moderator">Moderador</option>
                        <option value="technician">Técnico</option>
                        <option value="viewer">Visualizador</option>
                      </select>

                      {/* Desativar em vez de remover: guarda quem esteve na
                          equipe, e é o que o log de auditoria precisa para
                          explicar um lançamento antigo. Remover continua ali
                          para quem nunca devia ter entrado. */}
                      <button
                        onClick={() => alterarMembro(member, { status: inativo ? 'active' : 'inactive' })}
                        className="px-2.5 py-1.5 rounded-lg border border-slate-200 text-sm text-slate-600
                                   hover:bg-slate-50 whitespace-nowrap"
                      >
                        {inativo ? 'Reativar' : 'Desativar'}
                      </button>

                      {member.user_id !== user.id && (
                        <button
                          onClick={() => removeMember(member.id, member.profiles?.full_name)}
                          className="text-slate-400 hover:text-red-600 transition-colors p-2"
                          title="Remover do laboratório"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {labMembers.length === 0 && (
            <div className="text-center py-8 text-slate-500">
              <Users className="mx-auto mb-4 text-slate-400" size={48} />
              <p>Carregando membros da equipe...</p>
            </div>
          )}
        </div>
      )}

      {/* Contas onde o dinheiro entra */}
      {currentLab && (
        <ContasBancarias laboratoryId={currentLab.id} podeMexer={isAdmin || userRole === 'moderator'} />
      )}

      {/* Técnicos do laboratório */}
      {currentLab && (
        <div className="bg-white rounded-lg shadow-sm">
          <div className="px-6 py-4 border-b flex items-center space-x-3">
            <HardHat className="text-slate-400" size={20} />
            <div>
              <h3 className="text-lg font-medium text-slate-900">Técnicos</h3>
              <p className="text-sm text-slate-600">
                Quem executa OPU, FIV, TE e DG, e quem vai de ajudante. Não precisa ter login no sistema.
              </p>
            </div>
          </div>

          {podeCadastrarTecnico && (
            <div className="px-6 py-4 border-b bg-slate-50">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <input
                  type="text"
                  value={newTechnician.name}
                  onChange={(e) => setNewTechnician({ ...newTechnician, name: e.target.value })}
                  placeholder="Nome do técnico"
                  className="px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <input
                  type="text"
                  value={newTechnician.document}
                  onChange={(e) => setNewTechnician({ ...newTechnician, document: e.target.value })}
                  placeholder="Documento / CRMV (opcional)"
                  className="px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <input
                  type="text"
                  value={newTechnician.contact}
                  onChange={(e) => setNewTechnician({ ...newTechnician, contact: e.target.value })}
                  placeholder="Contato (opcional)"
                  className="px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {technicianError && (
                <div className="mt-3 bg-white border border-slate-300 text-slate-700 text-sm rounded-lg px-3 py-2">
                  {technicianError}
                </div>
              )}

              <button
                onClick={addTechnician}
                disabled={loading || !newTechnician.name.trim()}
                className="mt-3 flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                <Plus size={16} />
                <span>{loading ? 'Cadastrando...' : 'Cadastrar técnico'}</span>
              </button>
            </div>
          )}

          <div className="divide-y divide-slate-200">
            {technicians.map(tecnico => (
              <div key={tecnico.id} className="px-6 py-3">
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <p className={`font-medium truncate ${tecnico.active ? 'text-slate-900' : 'text-slate-400'}`}>
                    {tecnico.name}
                    {!tecnico.active && <span className="text-xs font-normal"> · inativo</span>}
                  </p>
                  {(tecnico.document || tecnico.contact) && (
                    <p className="text-xs text-slate-500 truncate">
                      {[tecnico.document, tecnico.contact].filter(Boolean).join(' · ')}
                    </p>
                  )}
                </div>
                {(podeCadastrarTecnico || podeGerarLink) && (
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {podeGerarLink && tecnico.active && (
                      <button
                        onClick={() => setLinkAberto(a => (a === tecnico.id ? null : tecnico.id))}
                        aria-expanded={linkAberto === tecnico.id}
                        className="text-sm text-slate-500 hover:text-slate-900 px-3 py-1 rounded hover:bg-slate-100"
                        title="Link para o técnico acompanhar a própria agenda"
                      >
                        Link da agenda
                      </button>
                    )}
                    {podeCadastrarTecnico && (<>
                    <button
                      onClick={() => toggleTechnician(tecnico)}
                      className="text-sm text-slate-500 hover:text-slate-900 px-3 py-1 rounded hover:bg-slate-100"
                      title={tecnico.active
                        ? 'Desativar: some da seleção, mas continua nos registros já feitos'
                        : 'Reativar'}
                    >
                      {tecnico.active ? 'Desativar' : 'Reativar'}
                    </button>
                    <button
                      onClick={() => removeTechnician(tecnico)}
                      className="text-slate-400 hover:text-red-600 transition-colors p-2"
                      title="Excluir do cadastro"
                    >
                      <Trash2 size={16} />
                    </button>
                    </>)}
                  </div>
                )}
              </div>
              {linkAberto === tecnico.id && <LinkDoCliente tecnico={tecnico} />}
              </div>
            ))}
          </div>

          {technicians.length === 0 && (
            <div className="text-center py-8 text-slate-500">
              <HardHat className="mx-auto mb-3 text-slate-400" size={40} />
              <p className="text-sm">Nenhum técnico cadastrado ainda.</p>
            </div>
          )}
        </div>
      )}

      {/* Modal - Criar Laboratório */}
      {showCreateLab && (
        <JanelaModal chave="criar-laboratorio" titulo="Criar Novo Laboratório" largura="sm:max-w-md"
          onFechar={() => {
            setShowCreateLab(false);
            setNewLabName('');
          }}>
          <div className="p-4 sm:p-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Nome do Laboratório
                </label>
                <input
                  type="text"
                  value={newLabName}
                  onChange={(e) => setNewLabName(e.target.value)}
                  placeholder="Ex: Lab Reprodução Bovina"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="flex gap-2 mt-6">
              <button
                onClick={createLaboratory}
                disabled={loading || !newLabName.trim()}
                className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? 'Criando...' : 'Criar'}
              </button>
              <button
                onClick={() => {
                  setShowCreateLab(false);
                  setNewLabName('');
                }}
                className="flex-1 bg-slate-500 text-white py-2 px-4 rounded-lg hover:bg-slate-600"
              >
                Cancelar
              </button>
            </div>
          </div>
        </JanelaModal>
      )}

      {/* Modal - Arquivar / Excluir Laboratório */}
      {removeTarget && (
        <JanelaModal chave="remover-laboratorio" titulo="Remover laboratório" largura="sm:max-w-md"
          subtitulo={<span className="font-medium text-slate-900">{removeTarget.lab.name}</span>}
          onFechar={() => setRemoveTarget(null)} fecharDesabilitado={loading}>
          <div className="p-4 sm:p-6">
            {removeTarget.loading && (
              <p className="text-sm text-slate-500 py-4">Verificando o que está vinculado...</p>
            )}

            {removeTarget.error && (
              <div className="bg-slate-50 border border-slate-300 text-slate-700 text-sm rounded-lg px-3 py-2">
                Não foi possível verificar os dados: {removeTarget.error}
              </div>
            )}

            {removeTarget.counts && (
              <>
                <div className="bg-slate-50 rounded-lg p-3 mb-4">
                  <div className="grid grid-cols-2 gap-y-1 text-sm">
                    <span className="text-slate-600">Clientes</span>
                    <span className="text-right font-medium">{removeTarget.counts.clients}</span>
                    <span className="text-slate-600">Serviços</span>
                    <span className="text-right font-medium">{removeTarget.counts.services}</span>
                    <span className="text-slate-600">Animais</span>
                    <span className="text-right font-medium">{removeTarget.counts.animals}</span>
                    <span className="text-slate-600">Embriões</span>
                    <span className="text-right font-medium">{removeTarget.counts.embryos}</span>
                  </div>
                </div>

                {removeTarget.counts.total === 0 ? (
                  <div className="bg-slate-50 border border-slate-200 text-slate-700 text-sm rounded-lg px-3 py-2 mb-4">
                    Este laboratório está vazio. Dá para excluir de vez, sem perder nada.
                  </div>
                ) : (
                  <div className="bg-slate-50 border border-slate-300 text-slate-700 text-sm rounded-lg px-3 py-2 mb-4">
                    {MARK.WARN} Arquivar tira o laboratório da lista, mas <strong>mantém todos os dados
                    salvos no banco</strong>. Dá para reativá-lo depois. A exclusão definitiva
                    fica bloqueada porque apagaria os registros acima junto.
                  </div>
                )}

                <div className="flex gap-2">
                  {removeTarget.counts.total === 0 ? (
                    <button
                      onClick={deleteLabPermanently}
                      disabled={loading}
                      className="flex-1 bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 disabled:opacity-50"
                    >
                      {loading ? 'Excluindo...' : 'Excluir'}
                    </button>
                  ) : (
                    <button
                      onClick={archiveLab}
                      disabled={loading}
                      className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                    >
                      {loading ? 'Arquivando...' : 'Arquivar'}
                    </button>
                  )}
                  <button
                    onClick={() => setRemoveTarget(null)}
                    disabled={loading}
                    className="flex-1 bg-white text-slate-700 border border-slate-300 py-2 px-4 rounded-lg hover:bg-slate-50 disabled:opacity-50"
                  >
                    Cancelar
                  </button>
                </div>
              </>
            )}
          </div>
        </JanelaModal>
      )}

      {/* Modal - Cadastrar Usuário */}
      {showAddUser && (
        <JanelaModal chave="cadastrar-usuario" titulo="Cadastrar Usuário" largura="sm:max-w-md"
          onFechar={() => {
            setShowAddUser(false);
            setNewUser({ username: '', password: '', full_name: '', role: 'moderator' });
            setAddUserError('');
          }}>
          <div className="p-4 sm:p-6">
            <p className="text-sm text-slate-600 mb-4">
              A conta é criada já ativa. Passe o usuário e a senha para a pessoa —
              ela pode trocar a senha depois.
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Nome completo
                </label>
                <input
                  type="text"
                  value={newUser.full_name}
                  onChange={(e) => setNewUser({ ...newUser, full_name: e.target.value })}
                  placeholder="Maria Silva"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Usuário
                </label>
                <input
                  type="text"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  value={newUser.username}
                  onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                  placeholder="maria.silva"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {/* Mostra o resultado da normalização enquanto se digita: é
                    exatamente isto que a pessoa vai ter que digitar no login,
                    e "Maria Silva" não entra como "Maria Silva". */}
                {newUser.username.trim() && (
                  <p className="text-xs text-slate-500 mt-1">
                    Vai entrar no sistema como <strong>{normalizarUsuario(newUser.username)}</strong>
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Senha provisória
                </label>
                <input
                  type="text"
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  placeholder="mínimo 8 caracteres"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Função na equipe
                </label>
                <select
                  value={newUser.role}
                  onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="moderator">Moderador (pode criar/editar tudo)</option>
                  <option value="technician">Técnico (pode atualizar etapas)</option>
                  <option value="viewer">Visualizador (apenas visualizar)</option>
                  <option value="admin">Administrador (controle total)</option>
                </select>
              </div>

              {addUserError && (
                <div className="bg-slate-50 border border-slate-300 text-slate-700 text-sm rounded-lg px-3 py-2">
                  {addUserError}
                </div>
              )}
            </div>

            <div className="flex gap-2 mt-6">
              <button
                onClick={addUser}
                disabled={loading || !normalizarUsuario(newUser.username) || newUser.password.length < 8}
                className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? 'Cadastrando...' : 'Cadastrar'}
              </button>
              <button
                onClick={() => {
                  setShowAddUser(false);
                  setNewUser({ username: '', password: '', full_name: '', role: 'moderator' });
                  setAddUserError('');
                }}
                className="flex-1 bg-slate-500 text-white py-2 px-4 rounded-lg hover:bg-slate-600"
              >
                Cancelar
              </button>
            </div>
          </div>
        </JanelaModal>
      )}
    </div>
  );
};

export default LabManagement;